import { Suspense, useCallback, type SyntheticEvent, type LazyExoticComponent, type ComponentType } from 'react'
import type { AdminAppState } from '../hooks/useAdminApp'
import type useSetupWizard from '../hooks/admin/useSetupWizard'
import type { AdminShellProps } from '../components/admin-shell/types'
import type { SetupWizardProps } from '../components/SetupWizard'
import type { SetupEntryScreenProps } from '../components/SetupEntryScreen'
import type { AuthScreenProps } from '../components/AuthScreen'
import EmporionSplashScreen from '../components/app/EmporionSplashScreen'
import UntrustedDeviceDialog from '../components/UntrustedDeviceDialog'

type AppContentMainViewProps = {
  mode: 'checking-setup' | 'onboarding-entry' | 'setup-wizard' | 'admin-shell' | 'auth-screen'
  app: AdminAppState
  setup: ReturnType<typeof useSetupWizard>
  handleOpenSetupEntry: () => void
  handleCommandOpenChange: (open: boolean) => void
  confirmLogout: () => void | Promise<void>
  handleLoginWithToast: (event: SyntheticEvent<HTMLFormElement>) => void | Promise<void>
  runPasskeyAuthWithToast: () => void | Promise<void>
  AdminShell: LazyExoticComponent<ComponentType<AdminShellProps>>
  SetupEntryScreen: LazyExoticComponent<ComponentType<SetupEntryScreenProps>>
  SetupWizard: LazyExoticComponent<ComponentType<SetupWizardProps>>
  AuthScreen: LazyExoticComponent<ComponentType<AuthScreenProps>>
}

const SectionLoader = () => <EmporionSplashScreen />

export const AppContentMainView = ({
  mode,
  app,
  setup,
  handleOpenSetupEntry,
  handleCommandOpenChange,
  confirmLogout,
  handleLoginWithToast,
  runPasskeyAuthWithToast,
  AdminShell,
  SetupEntryScreen,
  SetupWizard,
  AuthScreen,
}: AppContentMainViewProps) => {
  const handleAuthFormChange = useCallback(
    (next: Record<string, unknown>) => {
      app.setLoginForm((prev) => ({
        ...prev,
        ...next,
      }))
    },
    [app],
  )

  const handleBackToCredentials = useCallback(() => {
    app.setLoginStage('credentials')
    app.setLoginForm((prev) => ({ ...prev, otp: '', botProtectionToken: '', twoFactorSessionId: '' }))
  }, [app])

  if (mode === 'checking-setup') {
    return <EmporionSplashScreen />
  }

  if (mode === 'setup-wizard') {
    return (
      <Suspense fallback={<SectionLoader />}>
        <SetupWizard
          status={setup.setupStatus}
          loading={setup.setupLoading}
          submitting={setup.setupSubmitting}
          activationComplete={setup.setupSuccessVisible}
          error={setup.setupError}
          validationSummary={setup.setupValidationSummary}
          validating={setup.setupValidating}
          form={setup.setupForm}
          onOwnerFieldChange={setup.updateOwnerField}
          onStoreIdentityFieldChange={setup.updateStoreIdentityField}
          onOnboardingPreferenceFieldChange={setup.updateOnboardingPreferenceField}
          onPaymentSetupChange={setup.updatePaymentSetupField}
          onEmailSetupChange={setup.updateEmailSetupField}
          onBotProtectionSetupChange={setup.updateBotProtectionSetupField}
          onValidateConfiguration={setup.validateConfiguration}
          onSubmit={setup.completeSetup}
          onGoToLogin={setup.goToLogin}
        />
      </Suspense>
    )
  }

  if (mode === 'onboarding-entry') {
    return (
      <Suspense fallback={<SectionLoader />}>
        <SetupEntryScreen
          onBeginSetup={handleOpenSetupEntry}
        />
      </Suspense>
    )
  }

  if (mode === 'admin-shell') {
    return (
      <>
        <Suspense fallback={<SectionLoader />}>
          <AdminShell
            app={app}
            onOpenCommandPalette={() => handleCommandOpenChange(true)}
            onLogout={confirmLogout}
          />
        </Suspense>
        <UntrustedDeviceDialog
          open={app.requiresDeviceTrust}
          session={app.currentSession}
          trusting={app.sessionsLoading}
          onLogout={confirmLogout}
          onTrust={async () => {
            if (!app.currentSession?.id) return
            try {
              await app.trustSession(app.currentSession.id)
            } finally {
              if ('setRequiresDeviceTrust' in app && typeof app.setRequiresDeviceTrust === 'function') {
                app.setRequiresDeviceTrust(false)
              }
            }
          }}
        />
      </>
    )
  }

  return (
    <Suspense fallback={<SectionLoader />}>
      <AuthScreen
        loginForm={app.loginForm}
        loginStage={app.loginStage}
        authLoading={app.authLoading}
        onChange={handleAuthFormChange}
        onBackToCredentials={handleBackToCredentials}
        onPasskeyLogin={runPasskeyAuthWithToast}
        onSubmit={handleLoginWithToast}
      />
    </Suspense>
  )
}
